// Owner mark component (REQ-A11Y-002, component-a11y-spec §5) — the one place owner identity is
// rendered: a colour circle + white initial, stacked when an item has several owners. Colour is
// never the only signal — the initial and the aria-label name carry it too, so colour-blind and
// screen-reader users read the owner without relying on hue. Pure: no DOM, no I/O.
import { ownerColorIndex } from "./planner";
import { escapeHtml } from "./html";

export interface OwnerRef {
  id?: string | null;
  name: string;
  color?: string | null; // the member's chosen avatar colour (member_color); falls back to colorClass
}

export interface OwnerAvatarVM {
  initial: string; // the non-colour signal
  name: string; // the accessible label
  colorClass: string; // "owner-1".."owner-6" (0 → neutral) — the deterministic fallback
  color: string | null; // the member's chosen colour, or null → use colorClass
}

// ── Avatar colour palette (REQ-THEME-008) — a contrast-safe set a member picks their colour from.
// Every swatch passes white-text WCAG AA (≥4.5:1) so the white initial is legible, is ≥3:1 as a
// mark, and the set is mutually distinct so a family stays visually separable (component-a11y §5).
export const AVATAR_PALETTE: string[] = [
  "#b91c1c", "#db2777", "#a21caf", "#7c3aed", "#4f46e5", "#1d4ed8",
  "#0369a1", "#0f766e", "#15803d", "#4d7c0f", "#b45309", "#475569",
];

export interface MemberColorRef {
  id: string;
  name: string;
  color?: string | null;
}

/** Colours already taken by OTHER members → their initial (for the picker's "taken" marks). */
export function takenColours(members: MemberColorRef[], meId: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const m of members) {
    if (m.id === meId || !m.color) continue;
    out.set(m.color.toLowerCase(), initials(m.name));
  }
  return out;
}

/** First + last initial, uppercased ("?" when a name is blank). */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "?";
}

/** The view-model for each owner's avatar — initial + name + stable colour class. */
export function ownerAvatars(owners: OwnerRef[]): OwnerAvatarVM[] {
  return owners.map((o) => ({
    initial: initials(o.name),
    name: o.name,
    colorClass: `owner-${ownerColorIndex(o.id)}`,
    color: o.color ?? null,
  }));
}

/** Owner mark HTML — a single avatar, or a stacked cluster for multiple owners. The member's chosen
 *  colour (member_color) is applied inline when set, else the deterministic colour class. Empty
 *  string when there is no owner (unassigned rows show no mark). */
export function ownerMark(owners: OwnerRef[]): string {
  const avs = ownerAvatars(owners);
  if (!avs.length) return "";
  const one = (a: OwnerAvatarVM): string => {
    const cls = a.color ? "owner-av" : `owner-av ${a.colorClass}`;
    const style = a.color ? ` style="background:${escapeHtml(a.color)}"` : "";
    return `<span class="${cls}" role="img" aria-label="${escapeHtml(a.name)}"${style}>${escapeHtml(a.initial)}</span>`;
  };
  if (avs.length === 1) return one(avs[0]);
  const names = escapeHtml(avs.map((a) => a.name).join(", "));
  return `<span class="owner-stack" role="group" aria-label="${names}">${avs.map(one).join("")}</span>`;
}
